import React, { useEffect, useMemo } from 'react';
import ConfigProvider from 'antd/lib/config-provider';
import antdTheme from 'antd/lib/theme';
import { useSelector } from 'react-redux';
import { selectColorScheme } from '@src/features/interface/interfaceSlice';
import { getHostTheme } from './themeUtils';

type Props = {
  children: React.ReactNode;
  prefixCls?: string;
};

/**
 * Shared theme provider with common ConfigProvider configuration
 * Used to ensure consistent theming across the app and modals/drawers that render outside the main component tree
 * @param props - Component props containing children and optional prefix class
 * @returns React.JSX.Element
 */
const ThemeProvider = (props: Props) => {
  const { children, prefixCls = 'kemu' } = props;
  const colorScheme = useSelector(selectColorScheme);
  const hostTheme = useMemo(() => getHostTheme(colorScheme), [colorScheme]);
  const { defaultAlgorithm, darkAlgorithm } = antdTheme;

  useEffect(() => {
    document.documentElement.dataset.theme = colorScheme;
    document.body.dataset.theme = colorScheme;
    document.documentElement.style.colorScheme = colorScheme;
  }, [colorScheme]);

  const configTheme = useMemo(() => {
    const isDark = colorScheme === 'dark';

    return {
      algorithm: isDark ? darkAlgorithm : defaultAlgorithm,
      components: {
        Input: {
          colorBgContainer: hostTheme.colors.surface,
          activeBorderColor: hostTheme.colors.primary,
          colorText: hostTheme.colors.textPrimary,
          boxShadow: 'none',
          hoverBorderColor: hostTheme.colors.border,
          controlOutline: 'none',
        },
        Button: {
          colorPrimary: hostTheme.colors.primary,
        },
        Checkbox: {
          colorPrimary: hostTheme.colors.primary,
        },
        Typography: {
          fontFamily: 'Nunito',
        },
        Layout: {
          headerBg: hostTheme.colors.primary,
          bodyBg: hostTheme.colors.appBackground,
        },
      },
      // set to false to remove the 'css-dev-only' added in dev mode.
      // More info here: https://github.com/ant-design/ant-design/discussions/38753
      hashed: false,
      token: {
        colorPrimary: hostTheme.colors.primary,
        colorBgBase: hostTheme.colors.appBackground,
        colorBgContainer: hostTheme.colors.surface,
        colorBgElevated: hostTheme.colors.surface,
        colorBgMask: hostTheme.colors.overlay,
        colorBorder: hostTheme.colors.border,
        colorBorderSecondary: hostTheme.colors.border,
        colorFillContent: hostTheme.colors.surfaceMuted,
        colorFillTertiary: hostTheme.colors.surfaceMuted,
        colorSplit: hostTheme.colors.border,
        colorText: hostTheme.colors.textPrimary,
        colorTextBase: hostTheme.colors.textPrimary,
        colorTextDescription: hostTheme.colors.textSecondary,
        colorTextSecondary: hostTheme.colors.textSecondary,
        colorTextPlaceholder: hostTheme.colors.textSecondary,
        colorFillSecondary: hostTheme.colors.surfaceMuted,
        fontFamily: 'Nunito',
      },
    };
  }, [colorScheme, darkAlgorithm, defaultAlgorithm, hostTheme]);

  return (
    <ConfigProvider prefixCls={prefixCls} theme={configTheme}>
      {children}
    </ConfigProvider>
  );
};

export default ThemeProvider;
